import classNames from 'classnames';
import { Menu, Smartphone } from 'lucide-react';
import { bool, func, number, string } from 'prop-types';
import React, { useEffect, useState } from 'react';

import nowLogo from '../../../../assets/images/now-logo.png';
import { NamedLink } from '../../../../components';
import AuthIntentLink from '../../../../components/AuthIntentLink/AuthIntentLink';
import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { Events, mixPanelEventsData, mixPanelEventsScreens } from '../../../../mixpanel';
import { trackEventsMixpanel } from '../../../../mixpanel/mixpanel';
import GetAppModal from '../GetAppModal/GetAppModal';
import LanguageToggle from '../LanguageToggle/LanguageToggle';
import css from './TopbarMobileHeader.module.css';

const lucideProps = {
  fill: 'none',
  'aria-hidden': true,
};

const getLocalizedCmsPath = (pathname, language) => {
  const isArabic = language === 'Arabic';

  if (pathname === '/p/about' || pathname === '/p/about-arabic') {
    return isArabic ? '/p/about-arabic' : '/p/about';
  }

  if (pathname === '/privacy-policy' || pathname === '/privacy-policy-arabic') {
    return isArabic ? '/privacy-policy-arabic' : '/privacy-policy';
  }

  return pathname;
};

const TopbarMobileHeader = ({
  isAuthenticated,
  currentUser,
  merchantMaybe,
  notificationCount,
  onMenuOpen,
  intl,
  rootClassName,
  className,
}) => {
  const [getAppOpen, setGetAppOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState();

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const { userType } = currentUser?.attributes?.profile?.publicData || {};
  const isCustomer = userType === 'customer';
  const currentUserId = currentUser?.id?.uuid;
  const showVendorLogo = userType === 'provider' || merchantMaybe;
  const logoRouteName = showVendorLogo ? 'LandingPageMerchant' : 'LandingPage';

  useEffect(() => {
    const storedLanguage = typeof window !== 'undefined' && localStorage.getItem('language');
    if (storedLanguage) {
      setSelectedLanguage(storedLanguage);
    }
  }, []);

  const setLanguage = lg => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', lg);
      localStorage.setItem('rendered', lg);
      const { pathname, search, hash } = window.location;
      const localizedPath = getLocalizedCmsPath(pathname, lg);
      const nextUrl = `${localizedPath}${search}${hash}`;
      const currentUrl = `${pathname}${search}${hash}`;

      if (nextUrl !== currentUrl) {
        window.location.assign(nextUrl);
      } else {
        window.location.reload();
      }
    }
  };

  const handleLanguageChange = (lg, isCustomerUser) => {
    setLanguage(lg);
    setSelectedLanguage(lg);

    if (lg && isCustomerUser) {
      trackEventsMixpanel(Events.CUSTOMER_SIGNUP_COMPLETE, {
        action_type: mixPanelEventsData.CUSTOMER_SETUP_LANGUAGE,
        screen: mixPanelEventsScreens.NAV_BAR,
        user_type: 'customer',
        user_id: currentUserId,
        selected_language: lg?.toLowerCase(),
      });
    }
  };

  const languageToggle = (
    <LanguageToggle
      className={css.languageToggle}
      selectedLanguage={selectedLanguage}
      onToggle={lg => handleLanguageChange(lg, isCustomer)}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const menuButton = (
    <button
      type="button"
      className={css.menuButton}
      onClick={onMenuOpen}
      aria-label={intl.formatMessage({ id: 'Topbar.menuIcon' })}
    >
      <Menu className={css.menuIcon} size={20} strokeWidth={2} {...lucideProps} />
      {notificationDot}
    </button>
  );

  const guestRightBox = (
    <>
      <button
        type="button"
        className={css.getAppButton}
        onClick={() => setGetAppOpen(true)}
        aria-label={intl.formatMessage({ id: 'TopbarDesktop.getAppAriaLabel' })}
      >
        <span className={css.getAppIconWrap} aria-hidden="true">
          <Smartphone className={css.getAppIconSvg} size={16} strokeWidth={2} {...lucideProps} />
        </span>
        <span className={css.getAppLabel}>
          <FormattedMessage id="TopbarDesktop.getTheApp" />
        </span>
      </button>
      {languageToggle}
      <AuthIntentLink routeName="LoginPage" className={css.signinButton}>
        <span className={css.signinLabel}>
          <FormattedMessage id="TopbarDesktop.login1" />
        </span>
      </AuthIntentLink>
      {menuButton}
    </>
  );

  const authenticatedRightBox = (
    <>
      {languageToggle}
      {menuButton}
    </>
  );

  const classes = classNames(
    rootClassName || css.root,
    className,
    arabicLanguage && css.arabicDirection
  );

  return (
    <div className={classes}>
      <div className={css.headerOuter}>
        <div className={css.header}>
          <div className={css.wrapper}>
            <div className={classNames(css.logo, showVendorLogo && css.logoVendor)}>
              <NamedLink className={css.logoLink} name={logoRouteName}>
                <img src={nowLogo} alt="NOW Offers" className={css.logoImage} />
              </NamedLink>
            </div>

            <div className={css.rightBox}>
              {isAuthenticated ? authenticatedRightBox : guestRightBox}
            </div>
          </div>
        </div>
      </div>

      <GetAppModal
        isOpen={getAppOpen}
        onClose={() => setGetAppOpen(false)}
        arabicLanguage={arabicLanguage}
      />
    </div>
  );
};

TopbarMobileHeader.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  merchantMaybe: false,
  notificationCount: 0,
};

TopbarMobileHeader.propTypes = {
  rootClassName: string,
  className: string,
  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  merchantMaybe: bool,
  notificationCount: number,
  onMenuOpen: func.isRequired,
  intl: intlShape.isRequired,
};

export default TopbarMobileHeader;
